import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Chart } from 'chart.js';

export interface DatasetVisibilityState {
  label: string;
  color: string;
  visible: boolean;
  datasetIndex: number;
}

/**
 * Custom legend component for Chart.js correlation charts
 * Provides checkbox-based dataset visibility toggling with state preservation
 */
@Component({
    selector: 'app-correlation-legend',
    templateUrl: './correlation-legend.component.html',
    styleUrls: ['./correlation-legend.component.scss'],
    standalone: false
})
export class CorrelationLegendComponent implements OnInit, OnDestroy {
  @Input() chart: Chart;
  @Input() preserveVisibility: boolean = true;

  public datasetStates: DatasetVisibilityState[] = [];
  private visibilityMap = new Map<string, boolean>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Validate inputs
    if (!this.chart) {
      return;
    }

    this.updateDatasetStates();
    this.applyPreservedVisibility();
  }

  ngOnDestroy(): void {
    // Clean up any event listeners if needed
  }

  public updateDatasetStates(): void {
    if (!this.chart || !this.chart.data || !this.chart.data.datasets) {
      this.datasetStates = [];
      this.cdr.detectChanges();
      return;
    }

    this.datasetStates = this.chart.data.datasets.map((dataset, index) => {
      const label = dataset.label || `Dataset ${index}`;
      const color = (dataset.borderColor as string) || '#000000';

      // Preserve visibility state if available
      let visible = true;
      if (this.preserveVisibility && this.visibilityMap.has(label)) {
        visible = this.visibilityMap.get(label) ?? true;
      } else {
        visible = this.chart.isDatasetVisible(index);
      }

      return {
        label,
        color,
        visible,
        datasetIndex: index,
      };
    });

    // Sort dataset states by label alphabetically
    this.datasetStates.sort((a, b) => a.label.localeCompare(b.label));

    this.cdr.detectChanges();
  }

  public toggleDatasetVisibility(datasetState: DatasetVisibilityState): void {
    if (!this.chart || !this.chart.data || !this.chart.data.datasets) {
      return;
    }

    datasetState.visible = !datasetState.visible;

    this.chart.setDatasetVisibility(
      datasetState.datasetIndex,
      datasetState.visible
    );

    // Preserve visibility state
    this.visibilityMap.set(datasetState.label, datasetState.visible);

    // Update the chart
    try {
      this.chart.update('none'); // Use 'none' for better performance
    } catch (error) {
      console.warn('Chart update failed:', error);
    }
  }

  public applyPreservedVisibility(): void {
    if (
      !this.chart ||
      !this.preserveVisibility ||
      !this.chart.data ||
      !this.chart.data.datasets
    ) {
      return;
    }

    let appliedVisibility = false;
    this.chart.data.datasets.forEach((dataset, index) => {
      const label = dataset.label || `Dataset ${index}`;
      if (this.visibilityMap.has(label)) {
        const shouldBeVisible = this.visibilityMap.get(label);
        if (shouldBeVisible === undefined) {
          return;
        }
        this.chart.setDatasetVisibility(index, shouldBeVisible);
        appliedVisibility = true;

        // Update the local state
        const state = this.datasetStates.find((s) => s.datasetIndex === index);
        if (state) {
          state.visible = shouldBeVisible;
        }
      }
    });

    if (appliedVisibility) {
      try {
        this.chart.update('none');
      } catch (error) {
        console.warn('Chart update failed:', error);
      }
    }
  }

  public resetAllVisibility(): void {
    if (!this.chart || !this.chart.data || !this.chart.data.datasets) {
      return;
    }

    this.datasetStates.forEach((state) => {
      state.visible = true;
      this.visibilityMap.set(state.label, true);

      this.chart.setDatasetVisibility(state.datasetIndex, true);
    });

    try {
      this.chart.update('none');
    } catch (error) {
      console.warn('Chart update failed:', error);
    }
    this.cdr.detectChanges();
  }

  public hideAllExcept(exceptLabel: string): void {
    if (!this.chart || !this.chart.data || !this.chart.data.datasets) {
      return;
    }

    this.datasetStates.forEach((state) => {
      const shouldBeVisible = state.label === exceptLabel;
      state.visible = shouldBeVisible;
      this.visibilityMap.set(state.label, shouldBeVisible);

      this.chart.setDatasetVisibility(state.datasetIndex, shouldBeVisible);
    });

    try {
      this.chart.update('none');
    } catch (error) {
      console.warn('Chart update failed:', error);
    }
    this.cdr.detectChanges();
  }

  public trackByLabel(index: number, item: DatasetVisibilityState): string {
    return item.label;
  }

  /**
   * Computes the relative luminance of a color
   * @param color - Color in hex format (e.g., '#FF0000' or 'FF0000')
   * @returns Relative luminance value between 0 and 1
   */
  private getRelativeLuminance(color: string): number {
    // Remove # if present and normalize to 6 characters
    const hex = color.replace('#', '').padStart(6, '0');

    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    // Apply gamma correction
    const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

    // Calculate relative luminance
    return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB;
  }

  /**
   * Determines whether to use white or dark text based on background color contrast
   * @param backgroundColor - Background color in hex format
   * @returns 'white' for dark backgrounds, 'dark' for light backgrounds
   */
  public getTextColorForContrast(backgroundColor: string): 'white' | 'dark' {
    const luminance = this.getRelativeLuminance(backgroundColor);

    // Use white text on dark backgrounds (luminance < 0.5)
    // Use dark text on light backgrounds (luminance >= 0.5)
    return luminance < 0.5 ? 'white' : 'dark';
  }

  // Method to be called when chart is updated externally
  public onChartUpdate(): void {
    if (
      this.chart &&
      this.chart.data &&
      this.chart.data.datasets &&
      this.chart.data.datasets.length > 0
    ) {
      this.updateDatasetStates();
    } else {
      // Chart not ready yet, clear states
      this.datasetStates = [];
      this.cdr.detectChanges();
    }
  }
}
